// AaryaShree motion: reveals content gently as it scrolls into view, and drifts hero images.
// Does nothing for visitors who prefer reduced motion; content is fully visible without this script.
(() => {
  // Links like /#our-story: images above the section load after the browser's first jump and push it down,
  // so settle on the section again once the page has loaded.
  if (location.hash.length > 1) {
    const target = () => document.getElementById(decodeURIComponent(location.hash.slice(1)));
    const settle = () => target()?.scrollIntoView({ block: 'start', behavior: 'instant' });
    window.addEventListener('load', () => {
      settle();
      setTimeout(settle, 400);
    });
  }

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduce.matches) return;

  const root = document.documentElement;
  root.classList.add('as-motion');

  /** @type {IntersectionObserver | null} */
  let observer = null;

  const show = (el) => el.classList.add('is-visible');

  function observe(scope = document) {
    // Theme sections below the first one fade up as a whole
    scope.querySelectorAll('main > .shopify-section:not(:first-child)').forEach((section) => {
      // Tall sections (product grids) stay put so products are always visible straight away
      if (section.offsetHeight > window.innerHeight * 0.9) return;
      if (!section.querySelector('[data-as-reveal]') && !section.hasAttribute('data-as-reveal')) {
        section.setAttribute('data-as-reveal', '');
      }
    });

    // Children of a stagger group appear one after another
    scope.querySelectorAll('[data-as-stagger]').forEach((group) => {
      const step = Number(group.getAttribute('data-as-stagger')) || 110;
      [...group.children].forEach((child, i) => {
        if (!child.hasAttribute('data-as-reveal')) child.setAttribute('data-as-reveal', '');
        child.style.setProperty('--as-delay', String(i * step));
      });
    });

    const targets = scope.querySelectorAll('[data-as-reveal]:not(.is-visible)');
    if (!('IntersectionObserver' in window)) {
      targets.forEach(show);
      return;
    }
    observer ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            show(entry.target);
            observer?.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '0px 0px 10% 0px', threshold: 0 }
    );
    targets.forEach((el) => observer?.observe(el));
  }

  // Saree unfold on key images: collection cards, story images, and product cards on the homepage
  const UNFOLD = [
    '.collection-card__image',
    '.as-story__frame',
    '.as-weave__face--front',
  ];
  /** @type {IntersectionObserver | null} */
  let unfoldObserver = null;
  function unfold(scope = document) {
    const selectors = [...UNFOLD];
    if (document.body.dataset.asTemplate === 'index') selectors.push('.product-card .card-gallery');
    const targets = scope.querySelectorAll(selectors.join(','));
    if (!targets.length) return;
    unfoldObserver ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = /** @type {HTMLElement} */ (entry.target);
          el.classList.add('is-unfolded');
          unfoldObserver?.unobserve(el);
          setTimeout(() => el.querySelector(':scope > .as-unfold-panel')?.remove(), 2600);
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
    );
    let column = 0;
    targets.forEach((el) => {
      if (el.hasAttribute('data-as-unfold')) return;
      el.setAttribute('data-as-unfold', '');
      const panel = document.createElement('span');
      panel.className = 'as-unfold-panel';
      panel.setAttribute('aria-hidden', 'true');
      el.appendChild(panel);
      // cards in a row unfold one after another
      el.style.setProperty('--as-delay', String((column++ % 4) * 120));
      unfoldObserver?.observe(el);
    });
  }

  // Hero images drift slightly slower than the page
  const drifting = new Set();
  let ticking = false;
  function drift() {
    ticking = false;
    const vh = window.innerHeight;
    drifting.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) return;
      const shift = Math.max(-1, Math.min(1, rect.top / vh)) * -40;
      el.style.setProperty('--as-drift', `${shift.toFixed(1)}px`);
    });
  }
  function collectDrift(scope = document) {
    scope.querySelectorAll('[data-as-drift]').forEach((el) => drifting.add(el));
    drift();
  }
  window.addEventListener(
    'scroll',
    () => {
      if (ticking || drifting.size === 0) return;
      ticking = true;
      requestAnimationFrame(drift);
    },
    { passive: true }
  );

  function init() {
    observe();
    unfold();
    collectDrift();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: sections are re-rendered in place
  document.addEventListener('shopify:section:load', (event) => {
    const section = /** @type {HTMLElement} */ (event.target);
    observe(section);
    collectDrift(section);
    section.querySelectorAll('[data-as-reveal]').forEach(show);
    unfold(section);
    section.querySelectorAll('[data-as-unfold]').forEach((el) => el.classList.add('is-unfolded'));
  });
})();
